<template>
  <form v-if="value" @submit.prevent="onSubmit">
    <row>
      <column sm="6">
        <form-group label="Référence produit">
          <form-input
            type="text"
            v-model.trim="model.reference"
            placeholder="Ex : E12103"
          />
        </form-group>
      </column>
      <column sm="6">
        <form-group label=" ">
          <form-switch v-model="model.published"
            >Publier cette déclinaison</form-switch
          >
        </form-group>
      </column>
    </row>

    <form-group label="Description">
      <form-textarea v-model="model.description" placeholder />
    </form-group>

    <form-group label="Message affiché lors de la sélection du produit">
      <form-input type="text" v-model="model.message" />
      <form-radio
        class="display-inline-block mr-10"
        :required="model.message"
        name="messageType"
        value="notice"
        v-model="model.messageType"
      >
        Discret</form-radio
      >
      <form-radio
        class="display-inline-block mr-10"
        :required="model.message"
        name="messageType"
        value="info"
        v-model="model.messageType"
      >
        Information</form-radio
      >
      <form-radio
        class="display-inline-block"
        :required="model.message"
        name="messageType"
        value="warning"
        v-model="model.messageType"
      >
        Avertissement</form-radio
      >
    </form-group>

    <form-group label="Image">
      <row>
        <column lg="4" sm="6" v-for="image in product.images" :key="image.id">
          <figure
            class="thumbnail cursor-pointer"
            @click="model.image = image.id"
          >
            <i
              v-show="model.image === image.id"
              class="icon-check bg-success"
            ></i>
            <img
              :src="image | media('admin-thumbnail')"
              width="95"
              height="70"
              :alt="image.alt || image.name"
              class="img-thumbnail img-rounded"
              :class="{ 'border-success': model.image === image.id }"
            />
            <figcaption>{{ image.name }}</figcaption>
          </figure>
        </column>
      </row>
      <form-file-upload @input="onUploadedNewImage" image />
      <p slot="help">
        Choisissez parmi un fichier existant ou envoyez une nouvelle image
      </p>
    </form-group>

    <hr />

    <row>
      <column
        lg="6"
        v-for="(prop, propIndex) in product.properties"
        :key="prop.id || propIndex"
      >
        <form-group
          :label="prop.name"
          :warning="isDuplicate"
          :required="prop.required"
        >
          <form-select-color
            v-if="prop.kind === 'color'"
            v-model="modelAttributes[prop.id]"
            :colors="prop.colors"
            @input="testIsDuplicate"
            :buttonClass="isDuplicate ? `btn btn-warning` : ``"
            :placeholder="prop.required ? `Choisir...` : `Aucune`"
            :required="prop.required"
          />
          <form-select
            v-if="prop.kind === 'sex'"
            v-model="modelAttributes[prop.id]"
            :options="ProductPropertySexOptionsArray"
            @input="testIsDuplicate"
            :placeholder="prop.required ? `Choisir...` : `Aucun`"
            :required="prop.required"
          />
          <form-select
            v-if="prop.kind === 'tag'"
            v-model="modelAttributes[prop.id]"
            :options="getOptionsFromTags(prop.tags)"
            @input="testIsDuplicate"
            :placeholder="prop.required ? `Choisir...` : `Aucun`"
            :required="prop.required"
          />
          <form-input
            v-if="prop.kind === 'text'"
            type="text"
            v-model.trim="modelAttributes[prop.id]"
            placeholder="Entrer un texte"
            @input="testIsDuplicate"
            :required="prop.required"
          />
        </form-group>
      </column>
    </row>

    <p v-show="isDuplicate" class="text-warning text-semibold">
      Cette combinaison de propriétés est déjà définie dans une déclinaison.
    </p>

    <template v-if="variantImage">
      <hr />

      <row>
        <column lg="6">
          <form-group label="Zoom">
            <div class="pin-container">
              <img
                :src="variantImage | media('admin-preview')"
                width="285"
                height="210"
                :alt="variantImage.alt || variantImage.name"
                class="img-thumbnail img-rounded"
                @click="handleClickToPin"
              />
              <div
                v-for="({ x, y }, index) in model.pins"
                :key="index"
                @click="selectedPinIndex = index"
                class="pin"
                :class="{
                  selected: index === selectedPinIndex,
                  disabled: repositionningPin,
                }"
                :style="{
                  left: x * 100 + '%',
                  top: y * 100 + '%',
                }"
              >
                {{ index + 1 }}
              </div>
            </div>
          </form-group>
        </column>
        <column lg="6">
          <table class="table table-striped table-hover table-condensed">
            <thead>
              <tr>
                <td>Point</td>
                <td>Action</td>
              </tr>
            </thead>
            <tbody v-if="model.pins && model.pins.length">
              <tr
                @click="selectedPinIndex = index"
                v-for="({ image }, index) in model.pins"
                :key="index"
                class="selectable-row"
                :class="{ info: selectedPinIndex === index }"
              >
                <td width="100%">
                  {{ index + 1 }}
                  <span v-if="!image" class="text-warning">
                    (aucune image)
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    class="btn btn-danger btn-xs"
                    @click="removePin(index)"
                  >
                    <i class="icon-trash" />
                  </button>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="2" class="text-center">
                  Aucun point créé. Vous pouvez en créer un en cliquant sur
                  l'image ci-contre.
                </td>
              </tr>
            </tbody>
          </table>
          <template v-if="model.pins && model.pins[selectedPinIndex]">
            <hr />
            <h5>Point sélectionné :</h5>
            <form-group label="Image du point">
              <form-file-upload
                :key="selectedPinIndex"
                :value="model.pins[selectedPinIndex].image"
                @input="uploadSelectedPinImage"
                image
              />
              <p slot="help">Format d'image recommandé : JPG 1490x900px</p>
            </form-group>
            <button
              type="button"
              class="btn"
              @click="repositionningPin = !repositionningPin"
              :class="{ 'btn-primary': repositionningPin }"
            >
              Repositionner le point
            </button>
          </template>
        </column>
      </row>
    </template>

    <hr />

    <form-button :disabled="isDuplicate" kind="submit" type="submit"
      >Enregistrer</form-button
    >
    <form-button kind="cancel" @click="$emit('cancel')">Annuler</form-button>
  </form>
</template>

<style type="text/css" itemscope>
.thumbnail .icon-check {
  border-radius: 100%;
  height: 24px;
  left: 10px;
  line-height: 24px;
  position: absolute;
  top: 10px;
  width: 24px;
}
.thumbnail img {
  opacity: 0.75;
}
.thumbnail img.border-success {
  opacity: 1;
}

.selectable-row {
  cursor: pointer;
}

.pin-container {
  position: relative;
  cursor: pointer;
}

.pin {
  background: rgba(33, 150, 243, 0.5);
  border-radius: 100%;
  height: 24px;
  position: absolute;
  transform: translate(-50%, -50%);
  transition: transform 0.3s;
  width: 24px;
  text-align: center;
  line-height: 24px;
  font-size: 12px;
}

@keyframes pin-selected {
  0% {
    transform: translate(-50%, -50%) scale(1);
  }
  100% {
    transform: translate(-50%, -50%) scale(1.5);
  }
}

.pin:hover {
  transform: translate(-50%, -50%) scale(1.2);
}
.pin.selected {
  background: #2196f3;
}
.pin.disabled {
  opacity: 0.2;
  pointer-events: none;
}
</style>

<script>
import _get from "lodash/get";
import _merge from "lodash/merge";
import _omit from "lodash/omit";
import _keyBy from "lodash/keyBy";
import _map from "lodash/map";
import _mapValues from "lodash/mapValues";
import _isEqual from "lodash/isEqual";
import _pick from "lodash/pick";

import { ProductPropertySexOptionsArray } from "../../common/types";

export default {
  components: {
    FormStickyHeader: () => import("../../components/FormStickyHeader"),
    FormFileUpload: () => import("../../components/FormFileUpload"),
    FormSelectColor: () => import("../../components/FormSelectColor"),
    MediaImage: () => import("../../components/MediaImage"),
    FormSwitch: () =>
      import("package-common-vuejs/src/components/form/FormSwitch"),
    FormMultiselect: () =>
      import("package-common-vuejs/src/components/form/FormMultiselect"),
    OptionHtml: () => import("../../components/OptionHtml"),
  },
  props: {
    title: String,
    value: Object,
    product: Object,
  },
  data() {
    return {
      model: {},
      modelAttributes: {},
      isDuplicate: false,

      selectedPinIndex: 0,
      repositionningPin: false,

      ProductPropertySexOptionsArray,
    };
  },
  created() {
    this.updateModel(this.value);
  },
  watch: {
    value() {
      this.updateModel(this.value);
    },
    "model.message"() {
      if (this.model.message && !this.model.messageType)
        this.model.messageType = "info";
      if (!this.model.message) this.model.messageType = "";
    },
  },
  computed: {
    productProperties() {
      return _map(
        ((this.product && this.product.properties) || []).filter(
          ({ kind }) => kind !== "text"
        ),
        "id"
      );
    },
    variantImage() {
      return this.product.images.find(({ id }) => id === this.model.image);
    },
  },
  methods: {
    handleClickToPin(event) {
      const xMouse = event.offsetX;
      const yMouse = event.offsetY;
      const x = xMouse / event.target.offsetWidth;
      const y = yMouse / event.target.offsetHeight;
      if (this.repositionningPin) {
        this.model.pins.splice(this.selectedPinIndex, 1, {
          ...this.model.pins[this.selectedPinIndex],
          x,
          y,
        });
        this.repositionningPin = false;
      } else {
        this.model.pins.push({ x, y });
        this.selectedPinIndex = this.model.pins.length - 1;
      }
    },
    removePin(index) {
      this.model.pins.splice(index, 1);
    },
    uploadSelectedPinImage(image) {
      this.model.pins.splice(this.selectedPinIndex, 1, {
        ...this.model.pins[this.selectedPinIndex],
        image,
      });
    },
    updateModel(value) {
      this.model = {
        ...(value || {}),
        image: _get(value, "image.id"),
        pins:
          value.pins.map((pin) => ({
            ...pin,
            image: pin.image && pin.image.id,
          })) || [],
      };
      _merge(
        this.modelAttributes,
        _mapValues(_keyBy((value || {}).attributes, "property"), "value")
      );
    },
    getOptionsFromTags(tags) {
      return _map(tags, (tag) => ({ value: tag, label: tag }));
    },
    testIsDuplicate() {
      const propertiesToFind = this.productProperties;
      const attributesToFindObject = _pick(
        this.modelAttributes,
        propertiesToFind
      );
      const existingVariant = (
        (this.product && this.product.variants) ||
        []
      ).find(({ attributes }) => {
        const attributesObject = _pick(
          _mapValues(_keyBy(attributes, "property"), "value"),
          propertiesToFind
        );
        return _isEqual(attributesObject, attributesToFindObject);
      });
      this.isDuplicate =
        existingVariant && existingVariant.id !== this.value.id;
    },
    onSubmit() {
      this.$emit(
        "submit",
        _omit(
          {
            ...this.model,
            attributes: _map(this.modelAttributes, (value, property) => ({
              property,
              value,
            })),
            pins: _map(this.model.pins, (pin) => _omit(pin, "__typename")),
          },
          ["__typename"]
        )
      );
    },
    onUploadedNewImage(newImage) {
      this.model.image = newImage;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/products/ProductVariantForm.vue